import { API_ROUTE_CBO } from '../../ApiRoute';
import ApiService from '../../ApiService';
import ApiServiceBase from '../../ApiServiceBase';
import { ServerType, ServiceType } from '../../ServiceType.data';
import {
  IEpicDiagramPayload,
  IEpicDiagramResponse,
  IEpicsResponse,
  ITestCasesPayload,
  ITestCasesResponse,
  IUserStoriesForTestCasesResponse,
  IEpicsListResponse,
  IUserStoriesInBacklogBuildQAResponse,
  IUserStoriesInBacklogBuildQAPayload,
  IUserStoriesShapeResponse,
  ITestCasesBuild,
  IDefectsBySprint,
  IDefectsByPriority,
  ITestCasesByStatusResponse,
  ISprintHealthResponse,
  ISprintBurnDownResponse,
  ISprintwiseCapacityResponse,
} from '../../../models/cbo/AIAssistPortletsModel';
import { storage, STORAGE_CONSTANTS } from '../../LocalStorage';

/**
 * Service class for handling AI Assist Portlets related operations.
 */
export class AIAssistPortletsService {
  /**
   * ApiServiceBase instance for making API calls.
   */
  private readonly cboService: ApiServiceBase = new ApiService(ServiceType.None, ServerType.cbo);

  /**
   * Retrieves a list of epics which has diagrams based on the provided data.
   *
   * This method determines whether the current context is an engagement or a pursuit
   * by checking a value stored in local storage. Based on this context, it constructs
   * the appropriate API route to fetch the epics.
   *
   * @param data - A string representing the identifier used to replace the `:id` placeholder
   *               in the API route.
   * @returns A promise that resolves to an array of `IEpicsResponse` objects, or directly
   *          an array of `IEpicsResponse` objects.
   */
  public getEpics(data: string): Promise<IEpicsResponse[]> | IEpicsResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_EPICS : API_ROUTE_CBO.GET_PURSUIT_EPICS}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  /**
   * Retrieves an epic diagram based on the provided payload.
   *
   * @param {IEpicDiagramPayload} payload - The payload containing necessary data to fetch the epic diagram.
   * @returns {Promise<IEpicsResponse> | IEpicsResponse} A promise that resolves to an epic diagram or an epic diagram.
   */
  public getEpicDiagram(
    data: IEpicDiagramPayload,
  ): Promise<IEpicDiagramResponse> | IEpicDiagramResponse {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_EPIC_DIAGRAM : API_ROUTE_CBO.GET_PURSUIT_EPIC_DIAGRAM}`
        .replace(':id', data.engagementId)
        .replace(':isssueId', data.issueId),
    ];
    return this.cboService.get({ route: route, query: { diagramType: data.diagramType } });
  }

  /**
   * Retrieves test cases based on the engagement status.
   *
   * @param {string} data - The ID used to fetch the test cases.
   * @returns {Promise<IUserStoriesForTestCasesResponse[]> | IUserStoriesForTestCasesResponse[]} A promise that resolves to an array of test cases or an array of test cases.
   */
  public getUserStoriesForTestCases(
    data: string,
  ): Promise<IUserStoriesForTestCasesResponse[]> | IUserStoriesForTestCasesResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_USER_STORIES_FOR_TEST_CASES : API_ROUTE_CBO.GET_PURSUIT_USER_STORIES_FOR_TEST_CASES}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  /**
   * Retrieves test cases based on the provided payload.
   *
   * @param {ITestcasePayload} payload - The payload containing necessary data to fetch the test cases.
   * @returns {Promise<ITestCasesResponse[]> | ITestCasesResponse[]} A promise that resolves to an array of test cases or an array of test cases.
   */
  public getTestCases(
    payload: ITestCasesPayload,
  ): Promise<ITestCasesResponse[]> | ITestCasesResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_TEST_CASES : API_ROUTE_CBO.GET_PURSUIT_TEST_CASES}`
        .replace(':id', payload.engagementId)
        .replace(':issueId', payload.issueId),
    ];
    return this.cboService.get({ route: route });
  }

  /**
   * Retrieves a list of epics based on the engagement status.
   *
   * @param {string} data - The ID used to fetch the epics.
   * @returns {Promise<IEpicsListResponse[]> | IEpicsListResponse[]} A promise that resolves to an array of epics or an array of epics.
   */
  public getEpicsList(data: string): Promise<IEpicsListResponse[]> | IEpicsListResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_EPICS_LIST : API_ROUTE_CBO.GET_PURSUIT_EPICS_LIST}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  /**
   * Retrieves user stories in the backlog for Build QA based on the provided payload.
   *
   * This method determines whether the context is an engagement or a pursuit
   * and constructs the appropriate API route accordingly. It then makes a request
   * to fetch the user stories.
   *
   * @param data - The payload containing the necessary parameters to fetch the user stories.
   * @param data.engagementId - The ID of the engagement or pursuit.
   * @param data.type - The type of the user stories to retrieve.
   * @returns A promise resolving to an array of user stories in the backlog for Build QA,
   *          or directly an array of user stories, depending on the implementation.
   */
  public getUserStoriesInBacklogBuildQA(
    data: IUserStoriesInBacklogBuildQAPayload,
  ): Promise<IUserStoriesInBacklogBuildQAResponse[]> | IUserStoriesInBacklogBuildQAResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;
    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_USER_STORIES_BACKLOG_BUILD_QA : API_ROUTE_CBO.GET_PURSUIT_USER_STORIES_BACKLOG_BUILD_QA}`
        .replace(':id', data?.engagementId)
        .replace(':type', data?.type),
    ];
    return this.cboService.get({ route: route });
  }
  /**
   * Retrieves user stories based on the engagement status.
   *
   * @returns {Promise<IUserStoriesResponse[]> | IUserStoriesResponse[]} A promise that resolves to an array of user stories or an array of user stories.
   */
  public getUserStoriesShape(
    data: string,
  ): Promise<IUserStoriesShapeResponse[]> | IUserStoriesShapeResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_USER_STORIES_SHAPE : API_ROUTE_CBO.GET_PURSUIT_USER_STORIES_SHAPE}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getTestCasesBuild(data: string): Promise<ITestCasesBuild[]> | ITestCasesBuild[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_TEST_CASES_BUILD : API_ROUTE_CBO.GET_PURSUIT_TEST_CASES_BUILD}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getDefectsBySprint(data: string): Promise<IDefectsBySprint> | IDefectsBySprint {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_DEFECT_BY_SPRINT : API_ROUTE_CBO.GET_PURSUIT_DEFECTS_BY_SPRINT}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getDefectsByPriority(data: string): Promise<IDefectsByPriority> | IDefectsByPriority {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_DEFECT_BY_PRIORITY : API_ROUTE_CBO.GET_PURSUIT_DEFECTS_BY_PRIORITY}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getTestCasesByStatus(
    data: string,
  ): Promise<ITestCasesByStatusResponse[]> | ITestCasesByStatusResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_TEST_CASES_BY_STATUS : API_ROUTE_CBO.GET_PURSUIT_TEST_CASES_BY_STATUS}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getSprintHealth(data: string): Promise<ISprintHealthResponse> | ISprintHealthResponse {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_SPRINT_HEALTH : API_ROUTE_CBO.GET_PURSUIT_SPRINT_HEALTH}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  public getSprintBurnDown(
    data: string,
  ): Promise<ISprintBurnDownResponse> | ISprintBurnDownResponse {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_SPRINT_BURNDOWN : API_ROUTE_CBO.GET_PURSUIT_SPRINT_BURNDOWN}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }

  /**
   * Retrieves sprint-wise capacity data based on the provided identifier.
   *
   * This method determines whether the context is an engagement or a pursuit
   * and constructs the appropriate API route to fetch the sprint-wise capacity data.
   *
   * @param data - The identifier (e.g., engagement or pursuit ID) used to fetch the sprint-wise capacity data.
   * @returns A promise resolving to an array of `ISprintwiseCapacityResponse` objects
   *          or a direct array of `ISprintwiseCapacityResponse` objects.
   */
  public getSprintwiseCapacity(
    data: string,
  ): Promise<ISprintwiseCapacityResponse[]> | ISprintwiseCapacityResponse[] {
    const isEngagement = JSON.parse(
      storage.getItem(STORAGE_CONSTANTS.isEngagement) ?? 'false',
    ) as boolean;

    const route: string[] = [
      `${isEngagement ? API_ROUTE_CBO.GET_ENGAGEMENT_SPRINTWISE_CAPACITY : API_ROUTE_CBO.GET_PURSUIT_SPRINTWISE_CAPACITY}`.replace(
        ':id',
        data,
      ),
    ];
    return this.cboService.get({ route: route });
  }
}

const aiAssistPortletsService = new AIAssistPortletsService();
export default aiAssistPortletsService;
